<article class="doc">
  <header class="doc__header">
    <p class="doc__eyebrow">Atom</p>
    <h1 class="doc__title">Input</h1>
    <p class="doc__lead">
      Campo de texto de una línea con etiqueta, indicador obligatorio/opcional, máscara de
      importe (decimales configurables con <code>decimalPlaces</code>) y botón de
      visibilidad para contraseñas. Con <code>showPasswordRules</code> muestra checklist
      de fortaleza (8 caracteres, alfanumérico, mayúsculas y minúsculas). Borde normal
      <code>#dce0e8</code>, foco <code>#4169e2</code>.
    </p>
  </header>

  <!-- ── Estados ─────────────────────────────── -->
  <h2 class="doc-section-title">Estados</h2>

  <div class="doc-grid">
    <app-example-section
      title="Normal · Obligatorio"
      description="Estado por defecto con campo requerido."
    >
      <ui-input
        [block]="true"
        label="Nombre"
        placeholder="Nombre"
        [required]="true"
        kind="text"
      />
    </app-example-section>

    <app-example-section
      title="Normal · Opcional"
      description="Muestra 'Opcional' a la derecha."
    >
      <ui-input
        [block]="true"
        label="Notas"
        placeholder="Notas"
        [required]="false"
        [showOptionalHint]="true"
        kind="text"
      />
    </app-example-section>

    <app-example-section
      title="Error / inválido"
      description="Borde rojo. showMessage controla la visibilidad del texto de error de forma independiente."
    >
      <div class="doc-example-stack">
        <ui-input
          [block]="true"
          label="Nombre"
          placeholder="Nombre"
          [required]="true"
          [invalid]="isInvalid()"
          message="Este campo es requerido."
          [showMessage]="showErrorMsg()"
          kind="text"
        />
        <div class="doc-toggle-row">
          <button type="button" class="doc-native-toggle" (click)="toggleInvalid()">
            {{ isInvalid() ? 'Quitar error' : 'Activar error' }}
          </button>
          <button type="button" class="doc-native-toggle" (click)="toggleErrorMsg()">
            {{ showErrorMsg() ? 'Ocultar mensaje' : 'Mostrar mensaje' }}
          </button>
        </div>
      </div>
    </app-example-section>

    <app-example-section
      title="Deshabilitado"
      description="Sin interacción; fondo atenuado."
    >
      <ui-input
        [block]="true"
        label="Referencia"
        placeholder="Referencia"
        [required]="false"
        [disabled]="true"
        kind="text"
      />
    </app-example-section>

    <app-example-section
      title="Solo lectura"
      description="Muestra el valor sin permitir edición."
    >
      <ui-input
        [block]="true"
        label="Código"
        [value]="'ERP-2024-001'"
        [readonly]="true"
        [showOptionalHint]="false"
        kind="text"
      />
    </app-example-section>
  </div>

  <!-- ── Tipos ───────────────────────────────── -->
  <h2 class="doc-section-title">Tipos (UiInputKind)</h2>

  <div class="doc-grid">
    <app-example-section
      title="text"
      description="Texto libre de una línea."
    >
      <ui-input [block]="true" label="Nombre completo" placeholder="Nombre completo" kind="text" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="number"
      description="Número entero o decimal. Texto alineado a la derecha. Sin controles nativos de incremento/decremento (spinners)."
    >
      <ui-input [block]="true" label="Cantidad" placeholder="0" kind="number" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="amount"
      description="Importe monetario. Máscara con separador de miles. Por defecto 2 decimales al salir (decimalPlaces)."
    >
      <ui-input [block]="true" label="Importe" placeholder="0.00" kind="amount" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="amount · decimalPlaces"
      description="Misma máscara con más precisión. Ejemplo: 6 decimales (tasas/cuotas fiscales)."
    >
      <ui-input
        [block]="true"
        label="Tasa"
        placeholder="0.000000"
        kind="amount"
        [decimalPlaces]="6"
        [showOptionalHint]="false"
      />
    </app-example-section>

    <app-example-section
      title="nss · Número de seguridad social"
      description="Máscara XXXX-XX-XXXX-X. Solo acepta dígitos (11). Modelo con guiones."
    >
      <ui-input
        [block]="true"
        label="Número de seguridad social"
        placeholder="XXXX-XX-XXXX-X"
        kind="nss"
        [required]="true"
      />
    </app-example-section>

    <app-example-section
      title="email"
      description="Correo electrónico; teclado optimizado en móvil."
    >
      <ui-input [block]="true" label="Correo electrónico" placeholder="usuario@dominio.com" kind="email" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="password"
      description="Contraseña con botón para mostrar u ocultar el texto."
    >
      <ui-input [block]="true" label="Contraseña" placeholder="••••••••" kind="password" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="password · reglas"
      description="Con showPasswordRules muestra checklist: 8 caracteres, alfanumérico, mayúsculas y minúsculas."
    >
      <ui-input
        [block]="true"
        label="Nueva contraseña"
        placeholder="••••••••"
        kind="password"
        [showOptionalHint]="false"
        [showPasswordRules]="true"
        [value]="demoPassword()"
        (valueChange)="demoPassword.set($event)"
      />
    </app-example-section>

    <app-example-section
      title="password · confirmar"
      description="Con matchValue añade la regla visual 'Las contraseñas coinciden'."
    >
      <div class="doc-example-stack">
        <ui-input
          [block]="true"
          label="Nueva contraseña"
          placeholder="••••••••"
          kind="password"
          [showOptionalHint]="false"
          [showPasswordRules]="true"
          [value]="demoPassword()"
          (valueChange)="demoPassword.set($event)"
        />
        <ui-input
          [block]="true"
          label="Confirmar contraseña"
          placeholder="••••••••"
          kind="password"
          [showOptionalHint]="false"
          [matchValue]="demoPassword()"
          [value]="demoConfirmPassword()"
          (valueChange)="demoConfirmPassword.set($event)"
        />
      </div>
    </app-example-section>

    <app-example-section
      title="search"
      description="Campo de búsqueda; permite limpiar con × en algunos navegadores."
    >
      <ui-input [block]="true" label="Buscar" placeholder="Buscar..." kind="search" [showOptionalHint]="false" />
    </app-example-section>

    <app-example-section
      title="url"
      description="URL; valida formato básico en el navegador."
    >
      <ui-input [block]="true" label="Sitio web" placeholder="https://ejemplo.com" kind="url" [showOptionalHint]="false" />
    </app-example-section>
  </div>

  <!-- ── Ejemplos de uso ─────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Ejemplos de uso</h2>

    <p class="doc-block__p">Binding bidireccional con signal:</p>
    <pre class="doc-block__pre" role="code"><code>nombre = signal('');

&lt;ui-input
  label="Nombre"
  placeholder="Nombre"
  kind="text"
  [required]="true"
  [(value)]="nombre"
/&gt;</code></pre>

    <p class="doc-block__p">Con Reactive Forms:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-input
  label="Correo electrónico"
  kind="email"
  [required]="true"
  [invalid]="form.controls.email.invalid && form.controls.email.touched"
  message="Correo inválido."
  [showMessage]="form.controls.email.invalid && form.controls.email.touched"
  [value]="form.controls.email.value ?? ''"
  (valueChange)="form.controls.email.setValue($event)"
/&gt;</code></pre>

    <p class="doc-block__p">Importe con máscara automática (2 decimales por defecto):</p>
    <pre class="doc-block__pre" role="code"><code>importe = signal('');

&lt;ui-input
  label="Importe"
  placeholder="0.00"
  kind="amount"
  [required]="true"
  [(value)]="importe"
/&gt;
&lt;!-- importe() contiene el valor numérico limpio: "1234.56" --&gt;</code></pre>

    <p class="doc-block__p">Importe con precisión custom (<code>decimalPlaces</code>):</p>
    <pre class="doc-block__pre" role="code"><code>tasa = signal('');

&lt;ui-input
  label="Tasa"
  placeholder="0.000000"
  kind="amount"
  [decimalPlaces]="6"
  [required]="true"
  [(value)]="tasa"
/&gt;
&lt;!-- Máscara y toFixed usan 6 decimales; modelo limpio: "0.160000" --&gt;</code></pre>

    <p class="doc-block__p">Número de seguridad social (máscara <code>XXXX-XX-XXXX-X</code>):</p>
    <pre class="doc-block__pre" role="code"><code>nss = signal('');

&lt;ui-input
  label="Número de seguridad social"
  placeholder="XXXX-XX-XXXX-X"
  kind="nss"
  [required]="true"
  [(value)]="nss"
/&gt;
&lt;!-- Modelo formateado: "1234-56-7890-1" (solo dígitos al escribir) --&gt;</code></pre>

    <p class="doc-block__p">Error con mensaje controlado dinámicamente:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-input
  label="RFC"
  kind="text"
  [required]="true"
  [invalid]="rfcInvalido()"
  message="El RFC no tiene el formato correcto."
  [showMessage]="rfcInvalido() && touched()"
  [(value)]="rfc"
/&gt;</code></pre>

    <p class="doc-block__p">Solo lectura y ancho completo:</p>
    <pre class="doc-block__pre" role="code"><code>&lt;ui-input
  label="Folio"
  kind="text"
  [block]="true"
  [readonly]="true"
  [showOptionalHint]="false"
  [value]="folio()"
/&gt;</code></pre>

    <p class="doc-block__p">Contraseña con reglas visuales (opt-in):</p>
    <pre class="doc-block__pre" role="code"><code>password = signal('');
confirm = signal('');

&lt;ui-input
  label="Nueva contraseña"
  kind="password"
  [showPasswordRules]="true"
  [(value)]="password"
/&gt;
&lt;ui-input
  label="Confirmar contraseña"
  kind="password"
  [matchValue]="password()"
  [(value)]="confirm"
/&gt;
&lt;!-- Confirmación: solo "Las contraseñas coinciden" --&gt;</code></pre>
  </section>

  <!-- ── Inputs ─────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Inputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Defecto</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>kind</code></td>
          <td><code>UiInputKind</code></td>
          <td><code>'text'</code></td>
          <td>Tipo de campo; determina el comportamiento del teclado y la máscara (<code>text</code>, <code>number</code>, <code>amount</code>, <code>nss</code>, <code>email</code>, <code>password</code>, <code>search</code>, <code>url</code>)</td>
        </tr>
        <tr>
          <td><code>label</code></td>
          <td><code>string</code></td>
          <td><code>''</code></td>
          <td>Etiqueta visible; vinculada al input con <code>for/id</code></td>
        </tr>
        <tr>
          <td><code>value</code></td>
          <td><code>string</code> (model)</td>
          <td><code>''</code></td>
          <td>Valor del campo; soporta <code>[(value)]</code></td>
        </tr>
        <tr>
          <td><code>placeholder</code></td>
          <td><code>string</code></td>
          <td><code>''</code></td>
          <td>Texto de ayuda cuando el campo está vacío</td>
        </tr>
        <tr>
          <td><code>required</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Muestra "Obligatorio" y aplica <code>aria-required</code></td>
        </tr>
        <tr>
          <td><code>showOptionalHint</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Muestra "Opcional" cuando el campo no es requerido</td>
        </tr>
        <tr>
          <td><code>requiredHint</code></td>
          <td><code>string</code></td>
          <td><code>'Obligatorio'</code></td>
          <td>Texto personalizable del indicador de campo requerido</td>
        </tr>
        <tr>
          <td><code>optionalHint</code></td>
          <td><code>string</code></td>
          <td><code>'Opcional'</code></td>
          <td>Texto personalizable del indicador de campo opcional</td>
        </tr>
        <tr>
          <td><code>invalid</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Activa el estado de error (borde rojo)</td>
        </tr>
        <tr>
          <td><code>message</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Texto de ayuda o error bajo el campo</td>
        </tr>
        <tr>
          <td><code>showMessage</code></td>
          <td><code>boolean</code></td>
          <td><code>true</code></td>
          <td>Controla la visibilidad del mensaje independientemente de su contenido</td>
        </tr>
        <tr>
          <td><code>disabled</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Deshabilita el campo</td>
        </tr>
        <tr>
          <td><code>readonly</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>Muestra el valor sin permitir edición</td>
        </tr>
        <tr>
          <td><code>block</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>El componente ocupa el 100% del ancho del contenedor</td>
        </tr>
        <tr>
          <td><code>name</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Atributo <code>name</code> del input nativo</td>
        </tr>
        <tr>
          <td><code>autocomplete</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Valor del atributo <code>autocomplete</code></td>
        </tr>
        <tr>
          <td><code>maxlength</code></td>
          <td><code>number | undefined</code></td>
          <td><code>undefined</code></td>
          <td>Máximo de caracteres permitidos</td>
        </tr>
        <tr>
          <td><code>decimalPlaces</code></td>
          <td><code>number</code></td>
          <td><code>2</code></td>
          <td>
            Solo <code>kind="amount"</code>. Cantidad de decimales en la máscara
            al escribir y al perder el foco (<code>toFixed</code>). Ej. tasas/cuotas: <code>6</code>
          </td>
        </tr>
        <tr>
          <td><code>fieldId</code></td>
          <td><code>string | undefined</code></td>
          <td>Auto-generado</td>
          <td>Sobrescribe el <code>id</code> del input y el <code>for</code> del label</td>
        </tr>
        <tr>
          <td><code>showPasswordRules</code></td>
          <td><code>boolean</code></td>
          <td><code>false</code></td>
          <td>
            Solo <code>kind="password"</code>. Muestra checklist visual:
            8 caracteres, alfanumérico, mayúsculas y minúsculas
          </td>
        </tr>
        <tr>
          <td><code>matchValue</code></td>
          <td><code>string | undefined</code></td>
          <td><code>undefined</code></td>
          <td>
            Si se define, muestra la regla “Las contraseñas coinciden”.
            Independiente de <code>showPasswordRules</code>.
          </td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Outputs ────────────────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Outputs</h2>
    <table class="doc-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>valueChange</code></td>
          <td><code>OutputEmitter&lt;string&gt;</code></td>
          <td>Emite el nuevo valor en cada pulsación de tecla</td>
        </tr>
        <tr>
          <td><code>blurred</code></td>
          <td><code>OutputEmitter&lt;FocusEvent&gt;</code></td>
          <td>Emite cuando el campo pierde el foco</td>
        </tr>
        <tr>
          <td><code>focused</code></td>
          <td><code>OutputEmitter&lt;FocusEvent&gt;</code></td>
          <td>Emite cuando el campo recibe el foco</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ── Buenas prácticas ───────────────────── -->
  <section class="doc-block">
    <h2 class="doc-block__title">Buenas prácticas</h2>
    <ul class="doc-list">
      <li>
        Usa <code>[(value)]</code> para binding bidireccional con signals.
        Usa <code>[value]</code> + <code>(valueChange)</code> para Reactive Forms.
      </li>
      <li>
        Para <code>kind="amount"</code>, el modelo almacena el valor numérico limpio
        (ej. <code>"1234.56"</code>). La máscara formateada es solo visual.
        Convierte a <code>number</code> en la feature con <code>parseFloat()</code>.
        Ajusta la escala con <code>[decimalPlaces]</code> (default <code>2</code>);
        no cambia el resto de kinds ni el comportamiento por defecto de importes.
      </li>
      <li>
        Usa <code>[showMessage]</code> para mostrar el error solo después de que
        el usuario haya interactuado con el campo (<code>touched</code>), evitando
        mensajes prematuros al cargar el formulario.
      </li>
      <li>
        Para <code>kind="nss"</code> (Número de seguridad social), la máscara es
        <code>XXXX-XX-XXXX-X</code>: solo dígitos, máximo 11, guiones automáticos.
        El modelo guarda el valor formateado (ej. <code>"1234-56-7890-1"</code>).
      </li>
      <li>
        Los campos numéricos (<code>number</code>, <code>amount</code>, <code>nss</code>) alinean
        el texto a la derecha automáticamente para facilitar la lectura de cifras.
        En <code>kind="number"</code> se ocultan los spinners nativos del browser;
        el valor se captura solo por teclado (o pegado).
      </li>
      <li>
        Para fechas y horas usa <code>ui-date-input</code> en lugar de
        <code>kind="date"</code>: ofrece un selector visual consistente y máscara
        automática de separadores.
      </li>
      <li>
        No inyectar servicios en <code>UiInputComponent</code>: vive en
        <code>libs/shared/ui</code> y no debe tener dependencias de dominio.
      </li>
      <li>
        Usa <code>[showPasswordRules]="true"</code> solo cuando el flujo requiera
        validación visual de fortaleza. El login normal no lo activa para no
        alterar su UI. La regla de coincidencia va con <code>[matchValue]</code>
        en el campo de confirmación.
      </li>
    </ul>
  </section>
</article>
